import { Storage } from '@ionic/storage';
import { Drivers } from '@ionic/storage';
import GeoFAMemoizer from './GeoFAMemoizer';

/**
 * Global cache service - initialized once at app startup
 */
class CacheService {
    private storage: Storage | null = null;
    private memoizer: GeoFAMemoizer | null = null;
    private initialized: boolean = false;
    private initPromise: Promise<void> | null = null;

    /**
     * Initialize the cache service
     * Should be called once at app startup
     */
    async initialize(): Promise<void> {
        if (this.initialized) {
            return;
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = (async () => {
            try {
                // Configure storage with persistent drivers
                // Web: Uses IndexedDB (persistent) with LocalStorage fallback
                // Native: Automatically uses SQLite via Capacitor
                this.storage = new Storage({
                    name: '__frilufts_cache',
                    driverOrder: [
                        Drivers.IndexedDB,  // Web: Persistent, large capacity
                        Drivers.LocalStorage, // Web: Fallback, persistent
                        Drivers.SecureStorage // Native: Secure storage (SQLite is automatic on native)
                    ]
                });
                
                await this.storage.create();
                
                // Enable debug mode in development
                const debugMode = process.env.NODE_ENV === 'development';
                const memoizer = new GeoFAMemoizer(this.storage, debugMode);
                this.memoizer = memoizer;
                
                // Clean up expired cache entries on startup
                const expiredCount = await memoizer.cleanupExpired();
                if (expiredCount > 0) {
                    console.log(`🧹 Removed ${expiredCount} expired cache entries on startup`);
                }
                
                this.initialized = true;
                console.log('✅ CacheService initialized');
            } catch (error) {
                console.error('❌ Failed to initialize CacheService:', error);
                throw error;
            }
        })();

        return this.initPromise;
    }

    /**
     * Get the memoizer instance
     * Returns null if not initialized
     */
    getMemoizer(): GeoFAMemoizer | null {
        return this.memoizer;
    }

    /**
     * Get the storage instance
     * Returns null if not initialized
     */
    getStorage(): Storage | null {
        return this.storage;
    }

    /**
     * Check if cache is initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }
}

// Export singleton instance
export const cacheService = new CacheService();

